
import { useStore } from "@/store";
import {
  defineComponent,
  ref,
  computed,
  onBeforeUnmount,
  inject,
  onMounted,
} from "vue";
import { OrderStatus, OrderStatusColors } from "@/models/Enums";
import ViewHeader from "@/components/ViewHeader.vue";
import DropdownInput from "@/components/Inputs/DropdownInput.vue";
import moment from "moment";
import router from "@/router";
import { Customer, Order } from "@/models/Order";
import swal from "sweetalert2";
import { useI18n } from "vue-i18n";
import { api } from "@/services/Api";
import _ from "lodash";
import q from "q";
import { Emitter } from "mitt";

export default defineComponent({
  components: { ViewHeader, DropdownInput },
  async setup() {
    const store = useStore();
    const orderSourceOptions = computed(() => store.state.orderSources);
    const editCustomerMode = computed(() => store.state.editCustomerMode);
    const orderInStore = computed(() => store.state.order);
    const showArrivalConfirmationPoppup = ref(false);
    const oldCustomer = ref<Customer | null>(null);
    const oldRemarks = ref<string | null>(null);
    const oldSource = ref<number | null>(null);
    const { t } = useI18n();
    const emitter = inject("emitter") as Emitter<any>;

    orderInStore.value.customer.remarks = orderInStore.value.remarks;

    takeOldDataSnapshot();

    function takeOldDataSnapshot() {
      oldCustomer.value = _.clone(orderInStore.value.customer);
      oldRemarks.value = _.clone(orderInStore.value.customer.remarks);
      oldSource.value = _.clone(orderInStore.value.sourceOptionId);
    }

    function getOrderCreateDate(date: string) {
      return moment.utc(date).format("DD/MM/YYYY");
    }
    function arrivalConfirmation() {
      showArrivalConfirmationPoppup.value = true;
    }
    function onClockArrivalConfirmation(arrivalConfirmationType: number) {
      showArrivalConfirmationPoppup.value = false;
      store.commit("setArrivalConfirmationType", arrivalConfirmationType);
    }
    function backToResults() {
      store.commit("setOrder", new Order());
      router.replace({ name: "order-seach" });
    }
    function setEditCustomerMode(newVal: boolean) {
      store.commit("setEditCustomerMode", newVal);
    }
    function cancelChanges() {
      orderInStore.value.customer = _.clone(oldCustomer.value)!;
      orderInStore.value.remarks = _.clone(oldRemarks.value)!;
      orderInStore.value.customer.remarks = _.clone(oldRemarks.value)!;
      orderInStore.value.sourceOptionId = _.clone(oldSource.value);
      setEditCustomerMode(false);
    }
    async function saveChanges() {
      swal.showLoading();
      const responsePromise = api.SaveCustomerDetails(
        orderInStore.value.id,
        orderInStore.value.customer
      );
      await q.delay(400);
      const response = await responsePromise;
      if (response.errorMessage) {
        await swal.fire({
          icon: "error",
          title: t("error-pop-up.oops"),
          text: response.errorMessage,
        });
        return;
      }
      swal.close();
      onReservedOrderSaved();
    }
    function onReservedOrderSaved() {
      orderInStore.value.remarks = orderInStore.value.customer.remarks;
      takeOldDataSnapshot();
      setEditCustomerMode(false);
    }
    function acceptChanges() {
      setEditCustomerMode(false);
    }
    async function confirmSetZero(): Promise<boolean> {
      const result = await swal.fire({
        title: t('alert.order-status.zero-order'),
        icon: 'warning',
        customClass: {
          confirmButton: "popup-bookit-button",
          cancelButton: "close-button-wrapper popup-bookit-button",
        },
        buttonsStyling: false,
        showCancelButton: true,
        confirmButtonText: t('button.yes'),
        cancelButtonText: t('button.cancel')
      });
      return result.isConfirmed;
    }

    async function trySetOkStatus(orderId: number) {
      const confirm = await swal.fire({
        icon: "warning",
        title: t("alert.areYouSure"),
        showDenyButton: true,
        denyButtonText: t("alert.no"),
        confirmButtonText: t("alert.yes"),
        customClass: {
          confirmButton: "popup-bookit-button",
          denyButton: "close-button-wrapper popup-bookit-button",
        },
        buttonsStyling: false,
      });
      if (!confirm.isConfirmed) {
        return;
      }

      swal.showLoading();
      const result = await api.SetOrderStatus(orderId, OrderStatus.OK);
      await q.delay(400);

      if (result.errorMessage || result.data?.error) {
        await swal.fire({
          icon: "error",
          title: result.errorMessage ?? result.data?.error,
        });
        return;
      }

      orderInStore.value.status = OrderStatus.OK;
      orderInStore.value.statusName = "OK";
      swal.close();
    }

    async function cancelOrder(orderId) {
      const confirm = await swal.fire({
        icon: "warning",
        title: t("cashier.alert.cancel-order"),
        showDenyButton: true,
        denyButtonText: t("alert.no"),
        confirmButtonText: t("alert.yes"),
        customClass: {
          confirmButton: "popup-bookit-button",
          denyButton: "close-button-wrapper popup-bookit-button",
        },
        buttonsStyling: false,
      });
      if (!confirm.isConfirmed) {
        return;
      }
      await q.delay(200);
      var setZero = await confirmSetZero();
      const result = await api.withLoader().SetOrderStatus(orderId, OrderStatus.CN, setZero);
      if (result.errorMessage){
        await swal.fire({
          icon: "error",
          title: result.errorMessage
        });
        return;
      }
      orderInStore.value.status = OrderStatus.CN;
      orderInStore.value.statusName = "CN";
      if (setZero) {
        orderInStore.value.products.forEach(product => {
          product.priceLine.quantity = 0;
        });
      }
    }

    async function getOrderLinkUrl(orderId) {
      const result = await api.withLoader().GetOrderLinkUrl(orderId);
      
      if (result.errorMessage) {
          swal.fire({
              icon: 'error',
              text: result.errorMessage
          });
          return;
      }
    
      await swal.fire({
          title: t('orderActions.linkPopupTitle'),
          html: `<a href="${result.data}" target="_blank">${result.data}</a>`,
          cancelButtonText: t('button.close'),
          showConfirmButton: false,
          showCancelButton: true,
          showCloseButton: true
      });
    }

    function getOrderSourceOptionName(sourceId: number | null) {
      const defaultValue = "";
      if (!sourceId) {
        return defaultValue;
      }
      return (
        orderSourceOptions.value.find((x) => x.id == sourceId)?.text ??
        defaultValue
      );
    }
    onMounted(() => {
      emitter.on("reservedOrderSaved", onReservedOrderSaved);
    });
    onBeforeUnmount(() => {
      store.commit("setEditCustomerMode", false);
      emitter.off("reservedOrderSaved", onReservedOrderSaved);
    });
    return {
      orderInStore,
      editCustomerMode,
      showArrivalConfirmationPoppup,
      OrderStatus,
      OrderStatusColors,
      getOrderCreateDate,
      arrivalConfirmation,
      onClockArrivalConfirmation,
      backToResults,
      setEditCustomerMode,
      getOrderSourceOptionName,
      orderSourceOptions,
      cancelChanges,
      acceptChanges,
      trySetOkStatus,
      cancelOrder,
      saveChanges,
      getOrderLinkUrl
    };
  },
});
